import { ReactNode, useState } from 'react';

import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonList,
    ButtonListDirectionEnum,
    DirectionEnum,
    FlexBox,
    FlexItem,
    GapSize,
    Justify,
    Price,
    PriceLevel,
    RadioTile,
    RadioTiles,
    Text,
    TextLevels,
    Title,
    TitleLevels,
} from '@bytel/trilogy-react';
import { ButtonProps } from '@bytel/trilogy-react/lib/components/button/ButtonProps';

interface PlanConfiguratorProps {
    title: string;
    subtitle?: ReactNode;
    image?: string;
    price: number;
    sticker?: ReactNode;
    options: { id: string; label: string; description?: string; onOptionChange: () => void }[];
    defaultSelectedOptions?: string;
    nuggets?: ReactNode;
    buttons?: ButtonProps[];
    isGrid?: boolean;
    fullHeight: boolean;
}

export function PlanConfigurator({
    title,
    subtitle,
    image,
    price,
    sticker,
    options,
    defaultSelectedOptions,
    nuggets,
    buttons,
    isGrid = false,
    fullHeight,
}: Readonly<PlanConfiguratorProps>) {
    const [selected, setSelected] = useState(defaultSelectedOptions ?? options[0]?.id);

    return (
        <Box data-cy='plan-box' fullheight={fullHeight} shadowless>
            <BoxContent>
                <FlexBox direction={DirectionEnum.COLUMN} justify={Justify.SPACE_BETWEEN} fullheight>
                    <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
                        <FlexBox
                            justify={Justify.SPACE_BETWEEN}
                            align={Align.CENTER}
                            className='plan-configurator-header'
                        >
                            <FlexItem className='is-flex gap-medium' verticalAlign={Align.CENTER}>
                                {image && (
                                    <img
                                        src={image}
                                        alt=''
                                        style={{ height: '65px', width: '65px', objectFit: 'contain' }}
                                    />
                                )}
                                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.ONE}>
                                    <Title level={TitleLevels.THREE} marginless>
                                        {title}
                                    </Title>
                                    {subtitle && (
                                        <Text marginless level={TextLevels.TWO}>
                                            {subtitle}
                                        </Text>
                                    )}
                                </FlexBox>
                            </FlexItem>
                            <FlexItem narrow verticalAlign={Align.CENTER}>
                                <Price level={PriceLevel.FIVE} amount={price} period='mois' />
                            </FlexItem>
                        </FlexBox>
                        {sticker && <FlexItem className='has-text-centered'>{sticker}</FlexItem>}
                        {options && (
                            <FlexItem>
                                <RadioTiles className={isGrid ? 'grid-radio-list' : ''} data-cy='radio-list'>
                                    {options.map(({ id, label, description, onOptionChange }) => (
                                        <RadioTile
                                            key={id}
                                            id={id}
                                            name={id}
                                            horizontal
                                            checked={selected === id}
                                            onChange={() => {
                                                setSelected(id);
                                                onOptionChange();
                                            }}
                                            label={label}
                                            description={description}
                                        />
                                    ))}
                                </RadioTiles>
                            </FlexItem>
                        )}
                        {nuggets}
                    </FlexBox>
                    {buttons && (
                        <FlexItem narrow>
                            <ButtonList direction={ButtonListDirectionEnum.COLUMN}>
                                {buttons.map((buttonProps) => (
                                    <Button
                                        key={buttonProps.children as string}
                                        fullwidth
                                        markup={buttonProps.href ? 'a' : undefined}
                                        {...buttonProps}
                                    />
                                ))}
                            </ButtonList>
                        </FlexItem>
                    )}
                </FlexBox>
            </BoxContent>
        </Box>
    );
}
